import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { TextButton } from 'js/design_system/TextButton';

import {
  observeModalTables,
  observeTableOverflow,
} from 'js/journals/article/tableOverflowFade';

type ActiveTable = {
  wrap: HTMLElement;
};

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;

  background: none;
  border: 0;
  padding: 8px;

  color: var(--text-primary, #070a15);
  cursor: pointer;

  &:hover {
    color: var(--text-hover, #0c3b8d);
  }
`;

type TableCtaProps = {
  onOpen: () => void;
};

const TableCta: React.FC<TableCtaProps> = ({ onOpen }) => (
  <TextButton
    inline
    iconRight="arrow_outward"
    onClick={onOpen}
    trackClick={{
      source: 'journals.sap.expandable_tables.click_view_complete',
    }}
  >
    View complete table
  </TextButton>
);

// Cloning live DOM (vs. serializing to HTML and re-parsing) keeps rendered
// MathJax / inline-formulas intact — they don't survive a string round-trip.
export const prepareTableForModal = (wrap: HTMLElement): HTMLElement => {
  const clone = wrap.cloneNode(true) as HTMLElement;
  clone.removeAttribute('data-fade');

  // Strip ids from the clone so the document doesn't end up with duplicates
  // while the modal is open. The article-body copy keeps theirs, so post-close
  // hash navigation still resolves via getElementById.
  if (clone.id) clone.removeAttribute('id');
  clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

  const label = clone.querySelector('.sap__label');
  const caption = clone.querySelector('.sap__caption');
  const labelText = label?.textContent?.trim();
  if (label && caption && labelText) {
    const strong = document.createElement('strong');
    strong.textContent = `${labelText}. `;
    caption.insertBefore(strong, caption.firstChild);
    // The XSL emits a ". " text node immediately after the label element.
    // Remove it here so it doesn't appear as a stray period before the caption.
    const next = label.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE) {
      next.remove();
    }
    label.remove();
  }

  return clone;
};

export type ExpandableTableProps = {
  rootSelector: string;
};

export const ExpandableTable: React.FC<ExpandableTableProps> = ({
  rootSelector,
}) => {
  const [hosts, setHosts] = useState<
    Array<{ wrap: HTMLElement; host: HTMLElement }>
  >([]);
  const [active, setActive] = useState<ActiveTable | null>(null);

  const closeModal = useCallback(() => setActive(null), []);

  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const container = modalBodyRef.current;
    if (!container || !active) return undefined;

    container.appendChild(prepareTableForModal(active.wrap));
    const cleanup = observeModalTables(container);

    return () => {
      cleanup();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [active]);

  const handleModalBodyClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      if (!href.startsWith('#')) return;
      if (anchor.target && anchor.target !== '_self') return;
      const id = href.slice(1);
      if (!id) return;
      const targetEl = document.getElementById(id);
      if (!targetEl) return;
      event.preventDefault();
      setActive(null);
      // rAF so the scroll happens after the modal closes; while the dialog
      // is open, body has overflow:hidden and scrollIntoView would no-op.
      requestAnimationFrame(() => {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState(null, '', href);
      });
    },
    [],
  );

  useLayoutEffect(() => {
    const root = document.querySelector(rootSelector);
    if (!root) return undefined;

    const wraps = Array.from(
      root.querySelectorAll<HTMLElement>('.sap__table-wrap'),
    );
    if (wraps.length === 0) return undefined;

    const created = wraps.map((wrap) => {
      const host = document.createElement('div');
      host.setAttribute('data-sap-table-cta', '');
      wrap.parentNode?.insertBefore(host, wrap.nextSibling);
      const cleanup = observeTableOverflow(wrap, (fade) => {
        host.hidden = fade === 'none';
      });
      return { wrap, host, cleanup };
    });

    setHosts(created);

    return () => {
      created.forEach(({ host, cleanup }) => {
        cleanup();
        host.remove();
      });
      setHosts([]);
    };
  }, [rootSelector]);

  return (
    <>
      {hosts.map(({ wrap, host }, idx) =>
        createPortal(
          <TableCta onOpen={() => setActive({ wrap })} />,
          host,
          `sap-table-cta-${idx}`,
        ),
      )}
      <Modal
        open={active !== null}
        onClose={closeModal}
        showHeader={false}
        aria-label="Table viewer"
        containerStyle={{ maxWidth: '95vw' }}
      >
        <ModalContent>
          {active && (
            <>
              <CloseButton
                type="button"
                aria-label="Close"
                onClick={closeModal}
              >
                <Icon name="close" size={24} />
              </CloseButton>
              <div
                className="sap__article-body sap__table-wrap--in-modal"
                onClick={handleModalBodyClick}
                ref={modalBodyRef}
              />
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExpandableTable;
